import { useState } from 'owa-react';
import React from 'react';
import { logUsage, useRenderStats } from 'owa-analytics';
import {
    type ControlInMenuRendererFunction,
    type ControlRendererFunction,
    type RibbonTabDefinitionMLR,
    type RibbonTabDefinitionSet,
    type RibbonTabDefinitionSLR,
} from 'owa-acui';
import {
    getIsViewModeSelected,
    getRibbonViewMode,
    getLocalKeytipsEnabledStatus,
} from 'owa-command-ribbon-store';
import type { AppPaneUnderlayViewState } from 'owa-app-pane-underlay';
import { CommandRibbonBottomBar } from './CommandRibbonBottomBar';
import { CommandRibbonTopBar } from './CommandRibbonTopBar';
import { type CommandingViewMode } from 'owa-outlook-service-option-store/lib/store/schema/options/CommandingOptions';
import { type DatapointVariant } from 'owa-analytics-types';
import type { GovernedTask } from 'owa-tti/lib/types/GovernedTask';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import type { IconColorMap } from '../util/getRibbonIconColor';
import type { RibbonId } from 'owa-ribbon-ids';
import { RibbonKeytipLayer } from './RibbonKeytipLayer/RibbonKeytipLayer';
import { getStringRibbonId } from 'owa-ribbon-ids';
import { type MailRibbonTabId } from 'owa-ribbon-ids/lib/mailRibbonId';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { lazyGovern } from 'owa-tti';
import { observer } from 'owa-mobx-react';
import { RibbonContexts } from './RibbonContexts/RibbonContexts';
import { RibbonRoot } from './RibbonRoot/RibbonRoot';
import { RibbonTheme } from './RibbonTheme/RibbonTheme';
import { getSelectedTab } from '../util/getSelectedTab';
import { setRibbonScenarioName } from '../util/ribbonScenarioContext';
import { RibbonCustomizerContextMenu } from './RibbonCustomizerContextMenu';
import type { PerformanceDatapoint } from 'owa-analytics';

export type RibbonDefinitionResult = RibbonTabDefinitionSet & {
    scenarioDatapoint?: PerformanceDatapoint;
};

export interface CommandRibbonProps {
    getSingleLineRibbonDefinition: () => RibbonDefinitionResult;
    getMultiLineRibbonDefinition: () => RibbonDefinitionResult;
    iconColorMap: IconColorMap;
    ribbonScenarioName: string;
    controlRendererFunction: (c: ControlInMenuRendererFunction) => ControlRendererFunction;
    /** WI 400430 prioritizedSelectedMailTabId takes priority over getDefaultSelectedTabId.
     * We currently have multiple logic paths and modules (Calendar, People, Groups and Newsletters) relying on the existing selectedItem (getDefaultSelectedTabId),
     * so the transition will happen gradually. In the meantime, the mail module should prioritize prioritizedSelectedMailTabId:
     * if prioritizedSelectedMailTabId exists, we use it exclusively and ignore getDefaultSelectedTabId when populating the tab content. It's a function, since getMailCommandRibbonProps, it's computed.
     */
    prioritizedSelectedMailTabId?: (targetWindow: Window) => RibbonId;
    getDefaultSelectedTabId?: (targetWindow: Window) => RibbonId;
    onTabClick?: (tabId: string, targetWindow: Window) => void;
    getHideTopRibbon?: boolean;
    getExtraTopRibbonControlsBeforeTabs?: () => JSX.Element;
    getExtraTopRibbonControlsAfterTabs?: () => JSX.Element;
    copilotCommandCenter?: React.ReactNode;
    thisWindow?: Window;
    disableAnimation?: boolean;
    /** Prevents modal dialogs from adding a second keyTipLayer */
    noKeytipLayer?: boolean;
    /** Array of tasks to be lazyGovern'd on React.useEffect() */
    governedTasks?: GovernedTask[];
    projectionUnderlay?: AppPaneUnderlayViewState;
    showRibbonModeToggle?: boolean;
    isInDialog?: boolean;
    onCustomizeClick?: () => void;
}

const contentsStyle: React.CSSProperties = { display: 'contents' };

export const CommandRibbon: React.FC<CommandRibbonProps> = observer(props => {
    const startTime = Date.now();
    const mailboxInfo = getModuleContextMailboxInfo();

    useRenderStats('CommandRibbon_Render_Stats', mailboxInfo, 'CR', [], {
        customData: { isRibbonComposeOptEnabled: isGlobalFeatureEnabled('cmp-ribbon-compose-opt') },
    });

    React.useEffect(() => {
        if (props.governedTasks && props.governedTasks.length > 0) {
            lazyGovern.importAndExecute(...props.governedTasks);
        }
        // Log initial ribbon preference and only log it once
        logUsage(
            'RibbonPreference',
            mailboxInfo,
            { ribbonType: getRibbonViewMode() },
            { variant: 2 }
        );
    }, []);

    const targetWindow: Window = props.thisWindow ?? window;
    const prioritizedSelectedTabId = props.prioritizedSelectedMailTabId
        ? getStringRibbonId(props.prioritizedSelectedMailTabId(targetWindow))
        : undefined;
    const defaultSelectedTabId = props.getDefaultSelectedTabId
        ? getStringRibbonId(props.getDefaultSelectedTabId(targetWindow))
        : '';

    // Use useMemo to compute the effective selected tab ID, resetting when defaultSelectedTabId changes
    const [internalSelectedTabId, setInternalSelectedTabId] = useState<string | null>(null);
    const prevDefaultSelectedTabIdRef = React.useRef(defaultSelectedTabId);

    const selectedTabId = React.useMemo(() => {
        // If defaultSelectedTabId has changed, reset internal state and use the new default
        if (prevDefaultSelectedTabIdRef.current !== defaultSelectedTabId) {
            prevDefaultSelectedTabIdRef.current = defaultSelectedTabId;
            setInternalSelectedTabId(null);
            return defaultSelectedTabId;
        }

        // Use internal selection if user has made a choice, otherwise use default
        return internalSelectedTabId ?? defaultSelectedTabId;
    }, [defaultSelectedTabId, internalSelectedTabId]);

    const isSingleLineModeEnabled = (): boolean => {
        return getIsViewModeSelected(1) || isGlobalFeatureEnabled('cal-enable-hostedRibbon');
    };

    const isSinglelineMode: boolean = isSingleLineModeEnabled();

    const isProjectionPopout: boolean = targetWindow !== window;

    const [contextMenuX, setContextMenuX] = useState<number | null>(null);
    const [contextMenuY, setContextMenuY] = useState<number | null>(null);

    const onTabHeaderClick = React.useCallback(
        (tabId: string) => {
            if (props.onTabClick) {
                props.onTabClick(tabId, targetWindow);
            }

            setInternalSelectedTabId(tabId);
        },
        [props.onTabClick]
    );

    // Capture the scenario name during ribbon creation for logging
    setRibbonScenarioName(props.ribbonScenarioName);
    const { tabs, scenarioDatapoint } = isSinglelineMode
        ? props.getSingleLineRibbonDefinition()
        : props.getMultiLineRibbonDefinition();
    setRibbonScenarioName(undefined);

    const selectedTab: RibbonTabDefinitionSLR | RibbonTabDefinitionMLR = getSelectedTab(
        tabs,
        prioritizedSelectedTabId ? prioritizedSelectedTabId : selectedTabId,
        prioritizedSelectedTabId ? prioritizedSelectedTabId : defaultSelectedTabId,
        onTabHeaderClick
    );

    const selectedTabSupportsCustomization = React.useMemo(() => {
        return selectedTab.id == getStringRibbonId(1) || selectedTab.id == getStringRibbonId(3);
    }, [selectedTab.id]);

    const handleContextMenu = React.useCallback(
        (ev: React.MouseEvent) => {
            if (selectedTabSupportsCustomization) {
                ev.preventDefault();
                setContextMenuX(ev.clientX);
                setContextMenuY(ev.clientY);
            }
        },
        [selectedTabSupportsCustomization]
    );

    const handleDismissContextMenu = React.useCallback(() => {
        setContextMenuX(null);
        setContextMenuY(null);
    }, []);

    React.useEffect(() => {
        scenarioDatapoint?.addCustomData({
            CommandRibbonLoadedTime: Date.now(),
        });
    });

    scenarioDatapoint?.addCustomData({
        CommandRibbonRenderTime: Date.now(),
        CommandRibbonRenderDuration: Date.now() - startTime,
    });

    return (
        <>
            <div
                onContextMenu={
                    props.onCustomizeClick && isGlobalFeatureEnabled('mon-ribbon-mlr-customization')
                        ? handleContextMenu
                        : undefined
                }
                style={contentsStyle}
            >
                <RibbonTheme iconColorMap={props.iconColorMap}>
                    <RibbonRoot isInDialog={props.isInDialog} isInPopout={isProjectionPopout}>
                        <RibbonContexts appWindow={targetWindow}>
                            {!props.getHideTopRibbon && (
                                <CommandRibbonTopBar
                                    extraControlsBeforeTabs={props.getExtraTopRibbonControlsBeforeTabs?.()}
                                    extraControlsAfterTabs={props.getExtraTopRibbonControlsAfterTabs?.()}
                                    onTabHeaderClick={onTabHeaderClick}
                                    ribbonTabs={tabs}
                                    selectedTab={selectedTab}
                                />
                            )}

                            <CommandRibbonBottomBar
                                disableAnimation={props.disableAnimation}
                                isProjectionPopout={isProjectionPopout}
                                projectionUnderlay={props.projectionUnderlay}
                                selectedTab={selectedTab}
                                controlRendererFunction={props.controlRendererFunction}
                                copilotCommandCenter={props.copilotCommandCenter}
                                showRibbonModeToggle={props.showRibbonModeToggle}
                            />
                        </RibbonContexts>

                        <Keytips
                            inModal={!!props.noKeytipLayer}
                            inPopout={isProjectionPopout}
                            selectedTabId={
                                prioritizedSelectedTabId ? prioritizedSelectedTabId : selectedTabId
                            }
                        />
                    </RibbonRoot>
                </RibbonTheme>
            </div>
            {contextMenuX !== null &&
                contextMenuY !== null &&
                props.onCustomizeClick &&
                isGlobalFeatureEnabled('mon-ribbon-mlr-customization') && (
                    <RibbonCustomizerContextMenu
                        anchorX={contextMenuX}
                        anchorY={contextMenuY}
                        onDismiss={handleDismissContextMenu}
                        onCustomizeClick={props.onCustomizeClick}
                    />
                )}
        </>
    );
}, 'CommandRibbon');

/** Wrapping `RibbonKeytipLayer` with logic to determine when to render */
export interface KeytipsProps {
    inModal: boolean;
    inPopout: boolean;
    selectedTabId: string;
}
const Keytips = observer(({ inModal, inPopout, selectedTabId }: KeytipsProps) => {
    if (inModal || inPopout || getLocalKeytipsEnabledStatus() === false) {
        return null;
    }

    return <RibbonKeytipLayer selectedTabId={selectedTabId} />;
}, 'Keytips');
